import React, { useState, useContext } from 'react';
import { ApolloClient, InMemoryCache, gql } from '@apollo/client';
import { ThemeContext, ThemeProvider } from './ThemeContext';
import ThemeToggle from './components/ThemeToggle';
import VestingInfo from './components/VestingInfo';
import NetworkStats from './components/NetworkStats';
import './App.css';
import { ENDPOINTS } from './graphConfig';
import DelegationHelper from './components/DelegationHelper';

// Create Apollo clients for each network with your API key
const ethClient = new ApolloClient({
  uri: ENDPOINTS.ethereum.vesting,
  cache: new InMemoryCache()
});

const arbClient = new ApolloClient({
  uri: ENDPOINTS.arbitrum.vesting,
  cache: new InMemoryCache()
});

// GraphQL queries with periods field added
const GET_TOKEN_LOCK_WALLET = gql`
  query GetTokenLockWallet($id: ID!) {
    tokenLockWallet(id: $id) {
      id
      managedAmount
      startTime
      releaseStartTime
      endTime
      tokensReleased
      tokensWithdrawn
      tokensRevoked
      beneficiary
      periods
    }
  }
`;

const GET_TOKEN_LOCK_WALLETS_BY_BENEFICIARY = gql`
  query GetTokenLockWallets($beneficiary: String!) {
    tokenLockWallets(where: {beneficiary: $beneficiary}) {
      id
      managedAmount
      startTime
      releaseStartTime
      endTime
      tokensReleased
      tokensWithdrawn
      tokensRevoked
      beneficiary
      periods
    }
  }
`;

// Live addresses, re-checked against the subgraph before shipping. A broken
// example is worse than no example.
const EXAMPLES = [
  {
    title: 'A vesting contract',
    sub: '909K GRT · 58% released · ends 2027',
    address: '0xa0e4434b9f937c6beaf655a4cd093cbb9fcf3e92',
  },
  {
    title: 'A beneficiary',
    sub: '4 contracts · 0% to 100% released',
    address: '0x1f90acb4cdb238858c7347c9fbd8a1c44caa7928',
  },
];

function VestingSearch() {
  const [searchValue, setSearchValue] = useState('');
  const [network, setNetwork] = useState('eth');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [error, setError] = useState(null);

  const handleSearch = async (override) => {
    // Accepts an explicit address so the example chips can set the field and
    // search in the same click — setSearchValue would not have flushed yet.
    const query = typeof override === 'string' ? override : searchValue;
    if (!query) {
      setError('Please enter a wallet address');
      return;
    }
    
    setIsSearching(true);
    setError(null);
    setSearchResults(null);
    
    try {
      const client = network === 'eth' ? ethClient : arbClient;
      
      // First try as vesting wallet
      try {
        const vestingResponse = await client.query({
          query: GET_TOKEN_LOCK_WALLET,
          variables: { id: query.toLowerCase() }
        });
        
        if (vestingResponse.data.tokenLockWallet) {
          setSearchResults({
            type: 'vesting',
            data: [vestingResponse.data.tokenLockWallet]
          });
          setIsSearching(false);
          return;
        }
      } catch (err) {
        // Continue to try as beneficiary
      }
      
      // Try as beneficiary
      try {
        const beneficiaryResponse = await client.query({
          query: GET_TOKEN_LOCK_WALLETS_BY_BENEFICIARY,
          variables: { beneficiary: query.toLowerCase() }
        });
        
        if (beneficiaryResponse.data.tokenLockWallets && 
            beneficiaryResponse.data.tokenLockWallets.length > 0) {
          setSearchResults({
            type: 'beneficiary',
            data: beneficiaryResponse.data.tokenLockWallets
          });
        } else {
          setError('No vesting wallet or beneficiary found with this address');
        }
      } catch (err) {
        setError('No records found with this address');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(`Error searching: ${err.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  // Fixed format timestamp to readable date function
  const formatDate = (timestamp) => {
    // Special case for zero timestamp
    if (timestamp === "0" || Number(timestamp) === 0) {
      return "Same as start time";
    }
    
    // Make sure timestamp is a valid number
    const ts = Number(timestamp);
    
    // Check for invalid timestamps (negative or too small/large)
    if (isNaN(ts) || ts < 0) {
      console.warn("Invalid timestamp:", timestamp);
      return "Date unavailable";
    }
    
    // Always assume blockchain timestamps are in seconds
    const date = new Date(ts * 1000);
    
    // Check if date is valid
    if (date.toString() === "Invalid Date") {
      return "Date unavailable";
    }
    
    return date.toLocaleString();
  };

  // Format amount to show with token symbol
  const formatAmount = (amount) => {
    // Assuming amount is in wei (10^18)
    const formattedAmount = (Number(amount) / 10**18).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    });
    const symbol = 'GRT';
    return `${formattedAmount} ${symbol}`;
  };

  // Calculate period duration in days with vesting type description
  const calculatePeriodDuration = (wallet) => {
    const releaseStart = Number(wallet.releaseStartTime) || Number(wallet.startTime);
    const end = Number(wallet.endTime);
    const periods = Number(wallet.periods || 0);
    
    // If no periods defined or invalid data
    if (periods <= 0 || releaseStart >= end) {
      return null;
    }
    
    // Calculate period duration in days (total seconds / periods / seconds-per-day)
    const periodDurationDays = (end - releaseStart) / periods / 86400;
    
    // Add description based on period length
    let periodDesc = "";
    if (periodDurationDays >= 85 && periodDurationDays <= 95) {
      periodDesc = "quarterly vesting";
    } else if (periodDurationDays >= 28 && periodDurationDays <= 32) {
      periodDesc = "monthly vesting";
    } else if (periodDurationDays >= 6 && periodDurationDays <= 8) {
      periodDesc = "weekly vesting";
    } else if (periodDurationDays >= 360 && periodDurationDays <= 370) {
      periodDesc = "yearly vesting";
    } else if (periodDurationDays >= 180 && periodDurationDays <= 190) {
      periodDesc = "semi-annual vesting";
    }
    
    return `${periodDurationDays.toFixed(1)}${periodDesc ? ` days each, ${periodDesc}` : " days each"}`;
  };

  // Calculate vesting progress percentage with improved accuracy
  const calculateProgress = (wallet) => {
    const now = Date.now() / 1000;
    const startTime = Number(wallet.startTime);
    const releaseStartTime = Number(wallet.releaseStartTime);
    const endTime = Number(wallet.endTime);
    
    // Handle zero releaseStartTime - use startTime instead
    const effectiveReleaseStart = releaseStartTime === 0 ? startTime : releaseStartTime;
    
    // Before vesting starts
    if (now < startTime) return 0;
    
    // After vesting ends
    if (now >= endTime) return 100;
    
    // During cliff period (before release start)
    if (now < effectiveReleaseStart) {
      return ((now - startTime) / (endTime - startTime) * 100).toFixed(2);
    }
    
    // During linear vesting period
    return ((now - effectiveReleaseStart) / (endTime - effectiveReleaseStart) * 100).toFixed(2);
  };

  // Determine if vesting has a cliff structure
  const hasCliffVesting = (wallet) => {
    return Number(wallet.releaseStartTime) === Number(wallet.endTime);
  };

  // Handle the Enter key in the search input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="vesting-dashboard">
      <div className="search-container">
        <div className="search-options">
          <div className="network-toggle">
            <button 
              className={`network-button ${network === 'eth' ? 'active' : ''}`}
              onClick={() => setNetwork('eth')}
              aria-label="Search on Ethereum network"
            >
              Ethereum
            </button>
            <button 
              className={`network-button ${network === 'arb' ? 'active' : ''}`}
              onClick={() => setNetwork('arb')}
              aria-label="Search on Arbitrum network"
            >
              Arbitrum
            </button>
          </div>
        </div>
        
        <div className="search-input-container">
          <input
            type="text"
            placeholder="Enter any wallet address"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="search-input"
            aria-label="Wallet address input"
          />
          <button 
            onClick={handleSearch}
            disabled={isSearching}
            className="search-button"
            aria-label="Search for wallet"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>
        
        <div className="search-tip">
          <small>We'll automatically check both vesting and beneficiary wallets</small>
        </div>

        {/* Verified against the subgraph, not invented: the first is a contract
            mid-schedule (58% released, runs to 2027) so the progress display has
            something to show; the second is a beneficiary holding four contracts
            at 0%, 94%, 100% and 0%, which exercises the multi-wallet view. */}
        <div className="example-row">
          <span className="example-label">Try one:</span>
          {EXAMPLES.map((ex) => (
            <button
              key={ex.address}
              type="button"
              className="example-chip"
              title={ex.address}
              onClick={() => {
                setNetwork('eth');
                setSearchValue(ex.address);
                handleSearch(ex.address);
              }}
            >
              <span className="example-chip-title">{ex.title}</span>
              <span className="example-chip-sub">{ex.sub}</span>
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {searchResults && searchResults.data && (
        <div className="results-container">
          <h2>
            {searchResults.type === 'vesting' 
              ? 'Vesting Wallet Details' 
              : `Vesting Wallets for Beneficiary: ${searchValue}`}
          </h2>
          
          <div className="wallets-grid">
            {searchResults.data.map(wallet => (
              <div key={wallet.id} className="wallet-card">
                <h3>Vesting Wallet: {wallet.id}</h3>
                
                {wallet.beneficiary && (
                  <div className="detail-row">
                    <span className="detail-label">Beneficiary:</span>
                    <span className="detail-value">{wallet.beneficiary}</span>
                  </div>
                )}
                
                <div className="detail-row">
                  <span className="detail-label">Managed Amount:</span>
                  <span className="detail-value">{formatAmount(wallet.managedAmount)}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Start Time:</span>
                  <span className="detail-value">{formatDate(wallet.startTime)}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Release Start:</span>
                  <span className="detail-value">{formatDate(wallet.releaseStartTime)}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">End Time:</span>
                  <span className="detail-value">{formatDate(wallet.endTime)}</span>
                </div>
                
                {/* Show period information if available */}
                {wallet.periods && Number(wallet.periods) > 0 && calculatePeriodDuration(wallet) && (
                  <div className="detail-row vesting-schedule">
                    <span className="detail-label">Vesting Schedule:</span>
                    <span className="detail-value">
                      {`${wallet.periods} periods (~${calculatePeriodDuration(wallet)})`}
                    </span>
                  </div>
                )}
                
                {/* Display cliff vesting notice if applicable */}
                {hasCliffVesting(wallet) && (
                  <div className="detail-row vesting-type">
                    <span className="detail-label">Vesting Type:</span>
                    <span className="detail-value highlight">Cliff Vesting (all tokens release at end date)</span>
                  </div>
                )}
                
                <div className="detail-row">
                  <span className="detail-label">Tokens Withdrawn:</span>
                  <span className="detail-value">{formatAmount(wallet.tokensWithdrawn)}</span>
                </div>
                
                {wallet.tokensRevoked !== "0" && (
                  <div className="detail-row">
                    <span className="detail-label">Tokens Revoked:</span>
                    <span className="detail-value">{formatAmount(wallet.tokensRevoked)}</span>
                  </div>
                )}

                <div className="progress-container">
                  <div className="progress-label">
                    Vesting Progress: {calculateProgress(wallet)}%
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ 
                        width: `${calculateProgress(wallet)}%` 
                      }}
                    ></div>
                  </div>
                </div>
                
                <div className="detail-row tokens-available">
                  <span className="detail-label">Already Released:</span>
                  <span className="detail-value">
                    {formatAmount(
                      String(Math.max(0, Number(wallet.tokensReleased) - Number(wallet.tokensWithdrawn)))
                    )}
                  </span>
                </div>
                
                <div className="detail-row tokens-remaining">
                  <span className="detail-label">
                    {calculateProgress(wallet) >= 100 
                      ? "Unwithdrawn Balance:" 
                      : "Unwithdrawn Balance (Unvested):"}
                  </span>
                  <span className="detail-value">
                    {formatAmount(
                      String(Number(wallet.managedAmount) - 
                      Number(wallet.tokensReleased))
                    )}
                    {calculateProgress(wallet) >= 100 && " (Fully Vested)"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {searchResults && searchResults.data && (
        <DelegationHelper
          initialAddress={searchValue}
          initialNetwork={network === 'eth' ? 'ethereum' : 'arbitrum'}
        />
      )}
    </div>
  );
}

function AppContent() {
  const { theme } = useContext(ThemeContext);
  const [activeTab, setActiveTab] = useState('search');
  
  return (
    <div className="App" data-theme={theme}>
      <header className="App-header">
        <div className="theme-toggle-container">
          <ThemeToggle />
        </div>
        <div className="brand-container">
          <div className="logo-container">
            <a 
              href="https://graphtronauts.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="logo-link"
            >
              <div className="glowing-logo-container">
                <img 
                  src="https://graphtronauts.app/assets/logo-white-B7t0qwEV.svg" 
                  alt="Graphtronauts Logo" 
                  className="graphtronauts-logo glowing-logo" 
                />
              </div>
              <span className="graphtronauts-text">Graphtronauts</span>
            </a>
            <a 
              href="https://linktr.ee/graphtronauts" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="linktree-link"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              Linktree
            </a>
          </div>
        </div>
        <h1>Vesting Dashboard</h1>
        <p>Search for vesting details by entering any wallet address</p>
      </header>
      <main className="App-main">
        <div className="tab-container">
          <button 
            className={`tab ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
            aria-label="Search tab"
          >
            Search
          </button>
          <button 
            className={`tab ${activeTab === 'network' ? 'active' : ''}`}
            onClick={() => setActiveTab('network')}
            aria-label="Network stats tab"
          >
            Network
          </button>
          <button 
            className={`tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
            aria-label="Information tab"
          >
            Vesting Information
          </button>
        </div>
        
        {activeTab === 'search' && <VestingSearch />}
        {activeTab === 'network' && <NetworkStats client={ethClient} l2Client={arbClient} />}
        {activeTab === 'info' && <VestingInfo />}
      </main>
      <footer className="App-footer">
        <p>Vesting Dashboard - Powered by The Graph</p>
        <p className="footer-credit">Created by <a href="https://www.graphtronauts.com" target="_blank" rel="noopener noreferrer">Graphtronauts</a></p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
